import { useState, useEffect } from 'react'
import { Icon } from '@iconify/react'
import { toast } from 'react-toastify'
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from 'recharts'
import PageContainer from '../../components/layout/PageContainer'
import LoadingSpinner from '../../components/common/LoadingSpinner'
import Select from '../../components/common/Select'
import CompanyModal from '../../components/admin/CompanyModal'
import { dashboardService } from '../../services/api/dashboardService'
import { companyAdminService } from '../../services/api/companyAdminService'
import { formatDate } from '../../utils/formatters'

const SuperAdminDashboard = () => {
  const [loading, setLoading] = useState(true)
  const [period, setPeriod] = useState(30)
  const [showModal, setShowModal] = useState(false)
  const [modalMode, setModalMode] = useState('add')
  const [selectedCompany, setSelectedCompany] = useState(null)

  const [cards, setCards] = useState([
    { title: 'Total Registered Companies', value: '0', percentage: '' },
    { title: 'Total Users', value: '0', percentage: '' },
    { title: 'Total Revenue (All Time)', value: '$0', percentage: '' },
    { title: 'Active Subscriptions', value: '0' },
  ])
  const [monthlyRevenue, setMonthlyRevenue] = useState([])
  const [subscriptionStatus, setSubscriptionStatus] = useState({
    labels: ['Valid', 'Expiring Soon', 'Expired'],
    datasets: [
      {
        label: 'Subscription Status',
        data: [0, 0, 0],
        backgroundColor: ['#0058E8', '#FACC15', '#F43F5E'],
        borderWidth: 2,
        borderRadius: 6,
      },
    ],
  })
  const [tableData, setTableData] = useState([])

  const fetchDashboardData = async () => {
    setLoading(true)
    try {
      const data = await dashboardService.getDashboardData(period)

      if (data) {
        const { stats, subscriptionStatus: subStatus, earnings } = data

        if (stats) {
          setCards([
            {
              title: 'Total Registered Companies',
              value: stats.totalRegisteredCompanies?.toLocaleString() || '0',
              percentage: stats.totalRegisteredCompaniesChange
                ? `+${stats.totalRegisteredCompaniesChange}%`
                : '',
            },
            {
              title: 'Total Users',
              value: stats.totalUsers?.toLocaleString() || '0',
              percentage: stats.totalUsersChange ? `+${stats.totalUsersChange}%` : '',
            },
            {
              title: 'Total Revenue (All Time)',
              value: stats.totalRevenue ? `$${stats.totalRevenue.toLocaleString()}` : '$0',
              percentage: stats.totalRevenueChange ? `+${stats.totalRevenueChange}%` : '',
            },
            {
              title: 'Active Subscriptions',
              value: stats.activeSubscriptions?.toLocaleString() || '0',
            },
          ])
        }

        if (earnings && earnings.labels && earnings.data) {
          const chartData = earnings.labels.map((label, index) => ({
            month: label,
            revenue: earnings.data[index] || 0,
          }))
          setMonthlyRevenue(chartData)
        }

        if (subStatus) {
          const valid = subStatus.valid || 0
          const expiringSoon = subStatus.expiringSoon || 0
          const expired = subStatus.expired || 0

          setSubscriptionStatus({
            labels: ['Valid', 'Expiring Soon', 'Expired'],
            datasets: [
              {
                label: 'Subscription Status',
                data: [valid, expiringSoon, expired],
                backgroundColor: ['#0058E8', '#FACC15', '#F43F5E'],
                borderWidth: 2,
                borderRadius: 6,
              },
            ],
          })
        }
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error)
    } finally {
      setLoading(false)
    }
  }

  const fetchRecentCompanies = async () => {
    try {
      const data = await companyAdminService.list({ page: 1, limit: 5 })
      if (data?.results) {
        const formattedTableData = data.results.map((admin) => ({
          id: admin._id || admin.id,
          name: admin.fullName || `${admin.firstName} ${admin.lastName}`,
          email: admin.email || 'N/A',
          role: admin.planId?.name || 'N/A',
          status: admin.status
            ? admin.status.charAt(0).toUpperCase() + admin.status.slice(1)
            : 'Active',
          date: admin.createdAt ? formatDate(admin.createdAt) : 'N/A',
          rawData: admin,
        }))
        setTableData(formattedTableData)
      }
    } catch (error) {
      console.error('Error fetching recent companies:', error)
    }
  }

  useEffect(() => {
    fetchDashboardData()
    fetchRecentCompanies()
  }, [period])

  const handlePeriodChange = (e) => {
    setPeriod(parseInt(e.target.value))
  }

  const getStatusStyles = (status) => {
    const styles = {
      Active: { text: 'text-green-700', bg: 'bg-green-50' },
      Inactive: { text: 'text-gray-700', bg: 'bg-gray-50' },
      Pending: { text: 'text-yellow-700', bg: 'bg-yellow-50' },
      Blocked: { text: 'text-red-700', bg: 'bg-red-50' },
      Deleted: { text: 'text-red-700', bg: 'bg-red-50' },
    }
    return styles[status] || { text: 'text-gray-700', bg: 'bg-gray-50' }
  }

  const handleAddCompany = () => {
    setModalMode('add')
    setSelectedCompany(null)
    setShowModal(true)
  }

  const handleEditCompany = (company) => {
    setModalMode('edit')
    setSelectedCompany(company)
    setShowModal(true)
  }

  const handleModalClose = () => {
    setShowModal(false)
    setSelectedCompany(null)
  }

  const handleModalSuccess = () => {
    fetchDashboardData()
    fetchRecentCompanies()
  }

  const handleDeleteCompany = async (row) => {
    if (!row.id) return
    if (!window.confirm('Are you sure you want to delete this company admin?')) return

    try {
      await companyAdminService.delete(row.id)
      toast.success('Company admin deleted successfully')
      fetchRecentCompanies()
    } catch (error) {
      console.error('Error deleting company admin:', error)
      toast.error(error.response?.data?.message || 'Failed to delete company admin')
    }
  }

  const pieData = subscriptionStatus.labels.map((label, index) => ({
    name: label,
    value: subscriptionStatus.datasets[0].data[index] || 0,
    color: subscriptionStatus.datasets[0].backgroundColor[index],
  }))

  return (
    <PageContainer
      title="Super Admin Dashboard"
      subtitle="Overview of system metrics, companies, users, and subscriptions"
    >
      <div className="relative">
        {loading && (
          <div className="absolute inset-0 bg-white/50 flex items-center justify-center z-10 rounded-xl">
            <LoadingSpinner size="lg" />
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {cards.map((card, index) => (
            <div
              key={index}
              className="p-4 rounded-xl bg-white border border-border-light flex flex-col justify-between h-full"
            >
              <p className="text-text-secondary text-sm">{card.title}</p>
              <div className="flex justify-between items-center mt-2">
                <p className="text-xl font-semibold text-text-dark">{card.value}</p>
                {card.percentage && (
                  <div className="flex items-center gap-2">
                    <p className="text-sm text-green-600">{card.percentage}</p>
                    <Icon icon="mdi:trending-up" className="text-green-600" />
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-4">
          <div className="col-span-2 bg-white border border-border-light rounded-xl p-4">
            <div className="flex justify-between flex-wrap gap-3 mb-4">
              <h5 className="text-xl font-semibold text-text-dark">Earnings Revenue</h5>
              <Select
                value={period}
                onChange={handlePeriodChange}
                className="!py-2 text-sm"
                wrapperClassName=""
              >
                <option value={7}>Last 7 days</option>
                <option value={14}>Last 14 days</option>
                <option value={30}>Last 30 days</option>
              </Select>
            </div>
            <div className="h-[250px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={monthlyRevenue}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="month" tick={{ fontSize: 12 }} stroke="#9ca3af" />
                  <YAxis tick={{ fontSize: 12 }} stroke="#9ca3af" />
                  <Tooltip
                    formatter={(value) => [`$${value.toLocaleString()}`, 'Revenue']}
                    contentStyle={{ borderRadius: 8, border: '1px solid #e5e7eb' }}
                  />
                  <Bar dataKey="revenue" fill="#0058E8" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="bg-white border border-border-light rounded-xl p-4">
            <p className="text-sm font-semibold text-text-dark mb-4">Subscription Status</p>
            <div className="flex flex-col items-center gap-4">
              <div className="h-[180px]">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={pieData}
                      cx="50%"
                      cy="50%"
                      innerRadius={50}
                      outerRadius={80}
                      paddingAngle={2}
                      dataKey="value"
                    >
                      {pieData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip
                      formatter={(value, name) => [`${value}%`, name]}
                      contentStyle={{ borderRadius: 8, border: '1px solid #e5e7eb' }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="w-full">
                {subscriptionStatus.labels.map((label, index) => (
                  <div key={index} className="flex items-center gap-2 mb-3">
                    <div
                      className="w-2 h-2 rounded-full"
                      style={{ backgroundColor: subscriptionStatus.datasets[0].backgroundColor[index] }}
                    />
                    <div className="flex flex-1 items-center justify-between">
                      <p className="text-xs text-text-dark">{label}</p>
                      <p className="text-xs text-text-dark">
                        ({subscriptionStatus.datasets[0].data[index]}%)
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="mt-4 bg-white border border-border-light p-4 rounded-xl">
          <div className="flex flex-col md:flex-row items-center py-4 justify-between gap-2">
            <p className="text-sm font-semibold text-text-dark">New Company & User</p>
            <div className="flex items-center gap-2 ms-auto flex-wrap justify-end">
              <button className="flex items-center gap-2 text-sm font-semibold py-2 px-4 rounded-lg border border-border-light text-text-dark hover:bg-gray-50">
                <span>Filters</span>
                <Icon icon="mdi:filter-outline" className="text-lg" />
              </button>
              <button
                onClick={handleAddCompany}
                className="text-sm font-semibold bg-primary text-white py-2 px-4 rounded-lg hover:bg-primary-dark"
              >
                Add New Company
              </button>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-border-light">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-text-secondary uppercase">
                    User Name
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-text-secondary uppercase">
                    Email
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-text-secondary uppercase">
                    Subscription Plan
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-text-secondary uppercase">
                    Status
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-text-secondary uppercase">
                    Date
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-text-secondary uppercase">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border-light">
                {tableData.length > 0 ? (
                  tableData.map((row) => {
                    const statusStyles = getStatusStyles(row.status)
                    return (
                      <tr key={row.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary font-semibold text-sm">
                              {row.name.charAt(0).toUpperCase()}
                            </div>
                            <span className="text-sm text-text-dark">{row.name}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-text-dark">{row.email}</td>
                        <td className="px-4 py-3 text-sm text-text-dark">{row.role}</td>
                        <td className="px-4 py-3">
                          <span
                            className={`inline-flex items-center gap-1 rounded-full py-1 px-3 text-xs font-medium ${statusStyles.text} ${statusStyles.bg}`}
                          >
                            <Icon icon="mdi:circle" className="text-xs" />
                            {row.status}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-text-dark">{row.date}</td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            <button
                              onClick={() => handleDeleteCompany(row)}
                              className="text-text-secondary hover:text-red-600"
                            >
                              <Icon icon="mdi:delete-outline" className="text-lg" />
                            </button>
                            <button
                              onClick={() => handleEditCompany(row.rawData)}
                              className="text-text-secondary hover:text-primary"
                            >
                              <Icon icon="mdi:pencil-outline" className="text-lg" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    )
                  })
                ) : (
                  <tr>
                    <td colSpan={6} className="px-4 py-12 text-center">
                      <Icon
                        icon="mdi:account-multiple-outline"
                        className="mx-auto mb-2 text-4xl text-gray-300"
                      />
                      <p className="text-sm font-semibold text-text-dark">No companies found</p>
                      <p className="text-xs text-text-secondary">
                        New companies will appear here
                      </p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <CompanyModal
        show={showModal}
        onHide={handleModalClose}
        modalMode={modalMode}
        companyData={selectedCompany}
        onSuccess={handleModalSuccess}
      />
    </PageContainer>
  )
}

export default SuperAdminDashboard
